/**
 * Single shared browser geolocation read (deduped across CityContext, discovery, header).
 * Uses a module cache so multiple components don’t trigger multiple permission prompts.
 *
 * @returns {Promise<{ ok: true, lat: number, lng: number } | { ok: false, code?: number }>}
 */
const CACHE_MS = 10 * 60 * 1000;

let inflight = null;
let cached = null;

export function getUserCoordsOnce(options = {}) {
  if (typeof window === 'undefined' || !navigator.geolocation) {
    return Promise.resolve({ ok: false, code: 0 });
  }
  if (cached && Date.now() - cached.t < CACHE_MS) {
    return Promise.resolve({ ok: true, lat: cached.lat, lng: cached.lng });
  }
  if (inflight) return inflight;

  inflight = new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        cached = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          t: Date.now(),
        };
        resolve({ ok: true, lat: cached.lat, lng: cached.lng });
      },
      (err) => {
        const code = typeof err?.code === 'number' ? err.code : undefined;
        resolve({ ok: false, code });
      },
      {
        enableHighAccuracy: false,
        maximumAge: 300000,
        timeout: 12000,
        ...options,
      },
    );
  }).finally(() => {
    inflight = null;
  });

  return inflight;
}

/** GeolocationPositionError codes: 1 = PERMISSION_DENIED */
export const GEO_PERMISSION_DENIED = 1;

/** For tests / “use my location” flows that need a fresh read. */
export function clearUserCoordsCache() {
  cached = null;
}
